/**
 * Who is on the team, what hours they hold, and which of those hours count as hiring capacity.
 *
 * Split out of `rolePrioritisation.ts` for its line ceiling, and it is a real seam: everything here
 * answers "what capacity exists", while the rest of that module answers "what does the portfolio
 * demand". The main module re-exports all of it, so call sites import from either.
 *
 * The two predicates worth knowing before changing anything:
 *
 *  · `isPlanned` is TRUE by default and false only where `countsTowardHiring` is explicitly false.
 *    A new recruiter row is therefore capacity without anyone setting a flag, while the Head of TA
 *    sits on the team with her hours outside every total.
 *  · The intern pool is DERIVED from intern rows on the team, never typed. A number in a box let
 *    the tab plan on an intern nobody had added.
 */
import {
  UNASSIGNED,
  type CapacityAssumptions,
  type ClusterSeed,
  type PrioritisationState,
  type WorkingModel,
} from '../types/rolePrioritisation';

/** Committable hours yielded by one contracted hour. 0.72 at the seeded 20% / 10%. */
export function capacityYield(cap: CapacityAssumptions): number {
  return (1 - cap.overheadPct / 100) * (1 - cap.bufferPct / 100);
}

/** Committable hours of one 1.0 FTE week — 28.8 h at the seeded assumptions. */
export function fteHours(cap: CapacityAssumptions): number {
  return cap.fullWeekHours * capacityYield(cap);
}

export function personCapacity(p: WorkingModel, cap: CapacityAssumptions): number {
  return p.hours * capacityYield(cap);
}

export function isAssigned(p: WorkingModel): boolean {
  return p.cluster !== UNASSIGNED;
}

/**
 * Whether this person's hours are planned as hiring capacity.
 *
 * Absent means yes, so every existing row keeps its behaviour; only someone explicitly marked
 * `countsTowardHiring: false` — the Head of TA, a coordinator, someone on notice — is left out.
 */
export function isPlanned(p: WorkingModel): boolean {
  return p.countsTowardHiring !== false;
}

export function isIntern(p: WorkingModel): boolean {
  return p.isIntern === true;
}

/** Contracted hours across everyone carrying a cluster's load AND planned as capacity. */
export function grossHours(team: WorkingModel[]): number {
  return team.reduce((s, p) => s + (isAssigned(p) && isPlanned(p) ? p.hours : 0), 0);
}

export function headsIn(team: WorkingModel[], cluster: string): WorkingModel[] {
  return team.filter((p) => p.cluster === cluster && isPlanned(p));
}

/** On the team, in this cluster, but deliberately not planned — the lead, usually. */
export function unplannedIn(team: WorkingModel[], cluster: string): WorkingModel[] {
  return team.filter((p) => p.cluster === cluster && !isPlanned(p));
}

export function clusterFte(team: WorkingModel[], cluster: string, cap: CapacityAssumptions): number {
  return headsIn(team, cluster).reduce((s, p) => s + p.hours / cap.fullWeekHours, 0);
}

/**
 * Intern FTE the team actually holds and has not yet placed in a cluster.
 *
 * DERIVED, never typed. The old free-standing `poolCount` let the tab plan on an intern nobody had
 * added, and let the pool bar and the working-model table disagree about how many existed.
 */
export function internPoolFte(team: WorkingModel[], cap: CapacityAssumptions): number {
  return team.reduce(
    (s, p) => s + (isIntern(p) && isPlanned(p) && !isAssigned(p) ? p.hours / cap.fullWeekHours : 0),
    0
  );
}

/** Does the team hold any intern at all? The intern controls do not render when it does not. */
export function hasInterns(team: WorkingModel[]): boolean {
  return team.some((p) => isIntern(p) && isPlanned(p));
}

export function hoursLentBy(state: PrioritisationState, personIndex: number): number {
  return state.allocations.reduce((s, a) => s + (a.from === personIndex ? a.hours : 0), 0);
}

/** Hours offered by team members whose time is not planned as capacity. */
export function volunteeredHours(state: PrioritisationState): number {
  return state.allocations.reduce((s, a) => {
    const person = state.team[a.from];
    return s + (person && !isPlanned(person) ? a.hours : 0);
  }, 0);
}


/** What the team's own intern rows make available, less anyone out of office. */
export function poolAvailableFrom(
  state: PrioritisationState,
  cap: CapacityAssumptions
): number {
  return Math.max(internPoolFte(state.team, cap) - (Number(state.poolOut) || 0), 0);
}

export function poolAllocated(state: PrioritisationState, clusters: ClusterSeed[]): number {
  return clusters.reduce((s, c) => s + (state.interns[c.key] || 0), 0);
}

